/**
 * User-specific cache utilities for handling auth state changes
 * Provides smart cache invalidation to prevent stale data across user sessions
 */

import type { QueryClient } from "@tanstack/react-query";
import { queryCache } from "@/lib/data/shared/query-cache";

/**
 * Query key patterns that contain user-specific data
 * These need to be invalidated when auth state changes
 */
const USER_SPECIFIC_PATTERNS = [
	// Social data
	"facepile",
	"followerInteractions",
	"userStats",
	"followStatus",
	"followCounts",
	"notificationCount",

	// Reviews and ratings
	"userReview",
	"userSetReview",
	"userFestivalReview",
	"userShowReview",
	"mySchedule",
	"myReviews",

	// Profile data
	"userProfile",
	"userPreferences",
	"privacyStatus",
	"userArtists",
	"userFestivals",
	"userShows",

	// Festival/show attendance
	"festivalStatus",
	"userFestivalStatus",
	"attendance",
] as const;

/**
 * Clear all user-specific data from both TanStack Query and custom caches
 * Called on sign out and when switching users
 */
export function clearUserSpecificCaches(queryClient: QueryClient): void {
	// 1. Clear TanStack Query caches
	USER_SPECIFIC_PATTERNS.forEach((pattern) => {
		queryClient.removeQueries({
			predicate: (query) => {
				const key = query.queryKey.join(".");
				return key.includes(pattern);
			},
		});
	});

	// 2. Clear custom in-memory cache patterns
	// The queryCache uses pattern-based invalidation
	USER_SPECIFIC_PATTERNS.forEach((pattern) => {
		queryCache.invalidatePattern(pattern);
	});

	// 3. Also clear any cache entries that contain user IDs
	// This catches any patterns we might have missed
	queryCache.invalidatePattern("user_");
	queryCache.invalidatePattern(":user:");
}

/**
 * Invalidate caches for a specific user
 * Useful when user data changes (e.g., profile updates)
 */
export function invalidateUserCaches(
	queryClient: QueryClient,
	userId: string,
): void {
	// Invalidate TanStack Query caches with this user ID
	queryClient.invalidateQueries({
		predicate: (query) => {
			const key = query.queryKey.join(".");
			return key.includes(userId);
		},
	});

	// Invalidate custom cache entries
	queryCache.invalidatePattern(userId);
}

/**
 * Smart cache invalidation on auth state change
 * Preserves public data while clearing user-specific data
 */
export function handleAuthStateChange(
	queryClient: QueryClient,
	event: "SIGNED_IN" | "SIGNED_OUT" | "USER_UPDATED",
	userId?: string,
): void {
	switch (event) {
		case "SIGNED_OUT":
			// Clear all user-specific data
			clearUserSpecificCaches(queryClient);
			break;

		case "SIGNED_IN":
			// Clear any stale user data from previous sessions
			// This ensures the new user doesn't see old user's data
			clearUserSpecificCaches(queryClient);
			break;

		case "USER_UPDATED":
			// Only invalidate caches for the specific user
			if (userId) {
				invalidateUserCaches(queryClient, userId);
			}
			break;
	}

	// Public data (festivals, shows, artists) remains cached
	// This provides better performance for non-authenticated browsing
}

/**
 * Check if a query key contains user-specific data
 */
export function isUserSpecificQuery(queryKey: readonly unknown[]): boolean {
	const keyString = queryKey.join(".");
	return USER_SPECIFIC_PATTERNS.some((pattern) => keyString.includes(pattern));
}

/**
 * Generate user-scoped query keys
 * Ensures each user gets their own cache entries
 */
export function createUserScopedKey(
	baseKey: readonly unknown[],
	userId: string | null,
): readonly unknown[] {
	if (!userId) return baseKey;

	// Insert userId as second element to maintain consistency
	return [baseKey[0], "user", userId, ...baseKey.slice(1)];
}
